import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { API_BASE_URL } from '../../../../api/config';
import fetchService from '../../../../services/fetchService';

export const automation_update: SelfContainedCommand = {
  id: 'automation_update',
  description: `Update an existing automation. Call automation_list first to get the automationId, then provide only the fields to change.

**UPDATABLE FIELDS:**
- name, description, systemPrompt, enabled
- triggers: full replacement of all triggers (world_event, cron, manual)
- schedule: convenience shorthand — cron expression to add/replace cron trigger (pass "" to remove)
- model: LLM model preference
- maxDurationSeconds: run timeout in seconds
- computerUse: boolean — toggle computer-use mode on/off
- task: computer-use shorthand — updates systemPrompt + computerUseConfig.task
- credentialRef, tier, targetUserId: computer-use config fields
- category: app-context category (e.g. "Email", "Briefing")

**CRON SCHEDULE TIMEZONE RULE — CRITICAL:**
ALL cron expressions use UTC hours and minutes. Always set timezone to "UTC" when passing triggers. Never use the user's local timezone. Convert any local time to UTC before writing the cron expression.

**EXAMPLES:**
{ "automationId": "automation_abc123", "enabled": false }
{ "automationId": "automation_abc123", "schedule": "0 17 * * 1-5", "model": "claude-sonnet-4-6" }
{ "automationId": "automation_abc123", "triggers": [{ "type": "world_event", "eventType": "email.received" }] }`,
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',

  schema: {
    parameters: [
      { name: 'automationId', type: 'string', description: 'The ID of the automation to update', required: true },
      { name: 'name', type: 'string', description: 'New display name', required: false },
      { name: 'description', type: 'string', description: 'New description', required: false },
      { name: 'systemPrompt', type: 'string', description: 'New system prompt / standing instructions', required: false },
      { name: 'task', type: 'string', description: 'Computer-use shorthand — updates both systemPrompt and computerUseConfig.task', required: false },
      { name: 'enabled', type: 'boolean', description: 'Enable or disable the automation', required: false },
      { name: 'triggers', type: 'array', description: "Full trigger replacement array. Each: { type: 'world_event'|'cron'|'manual', ... }. Cron hours/minutes are always UTC.", required: false },
      { name: 'schedule', type: 'string', description: 'Cron shorthand (UTC) — adds/replaces cron trigger. Hours/minutes must be UTC. Pass "" to remove. Ignored when triggers is provided.', required: false },
      { name: 'model', type: 'string', description: 'LLM model preference. Pass "" to reset to default.', required: false },
      { name: 'maxDurationSeconds', type: 'number', description: 'Run timeout in seconds', required: false },
      { name: 'computerUse', type: 'boolean', description: 'Enable or disable computer-use mode', required: false },
      { name: 'credentialRef', type: 'string', description: 'Computer-use: service name or credential ID from PasswordVault', required: false },
      { name: 'tier', type: 'string', description: 'Computer-use tier (auto, dom, screenshot)', required: false },
      { name: 'targetUserId', type: 'string', description: 'Computer-use: auth0Id of org member whose computer runs this automation', required: false },
      { name: 'category', type: 'string', description: 'App-context category (e.g. "Email", "Briefing")', required: false },
    ],
  },

  examples: {
    good: [
      {
        input: { automationId: 'automation_abc123', enabled: false },
        expectedOutput: createSuccessResult('Updated automation', { automationId: 'automation_abc123', name: 'My Automation' }),
        description: 'Disable an automation',
      },
      {
        input: {
          automationId: 'automation_abc123',
          triggers: [{ type: 'world_event', eventType: 'email.received' }, { type: 'cron', cron: '0 8 * * 1' }],
        },
        expectedOutput: createSuccessResult('Updated automation', { automationId: 'automation_abc123', name: 'My Automation' }),
        description: 'Replace all triggers on an automation',
      },
    ],
    bad: [
      {
        input: { name: 'New Name' },
        error: 'automationId is required',
        why: 'Must identify which automation to update',
        fix: 'Use automation_list to find the automation ID first',
      },
    ],
  },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const {
      automationId,
      name,
      description,
      systemPrompt,
      task,
      enabled,
      triggers,
      schedule,
      model,
      maxDurationSeconds,
      computerUse,
      credentialRef,
      tier,
      targetUserId,
      category,
    } = params;

    if (!automationId) return createErrorResult('automationId is required', '', 'validation');

    const updates: Record<string, any> = {};

    if (name !== undefined) updates.name = name;
    if (description !== undefined) updates.description = description;
    if (enabled !== undefined) updates.enabled = enabled;
    if (model !== undefined) updates.modelPreference = model === '' ? null : model;
    if (maxDurationSeconds !== undefined) updates.maxDurationSeconds = maxDurationSeconds;
    if (computerUse !== undefined) updates.computerUse = computerUse;
    if (category !== undefined) updates.category = category;

    // computer-use task shorthand
    if (task !== undefined) {
      let existing: any = {};
      try {
        existing = await fetchService(`${API_BASE_URL}/automations/${automationId}`, { method: 'GET' });
      } catch { /* ignore */ }
      const currentCUConfig = existing.skeleton?.computerUseConfig || {};
      const currentCredRef = credentialRef ?? currentCUConfig.credentialRef;
      const credSuffix = currentCredRef ? ` When calling computer_use_run, pass credentialRef: "${currentCredRef}".` : '';
      updates.systemPrompt = `You are a computer-use automation. Your task is: ${task}${credSuffix}`;
      updates.computerUse = true;
      updates.computerUseConfig = {
        ...currentCUConfig,
        task,
        ...(tier !== undefined ? { tier } : {}),
        requiresElectron: true,
        ...(currentCredRef ? { credentialRef: currentCredRef } : {}),
        ...(targetUserId !== undefined ? { targetUserId } : {}),
      };
    } else {
      if (systemPrompt !== undefined) updates.systemPrompt = systemPrompt;

      if (tier !== undefined || credentialRef !== undefined || targetUserId !== undefined) {
        let existing: any = {};
        try {
          existing = await fetchService(`${API_BASE_URL}/automations/${automationId}`, { method: 'GET' });
        } catch { /* ignore */ }
        const currentCUConfig = existing.skeleton?.computerUseConfig || {};
        updates.computerUseConfig = {
          ...currentCUConfig,
          ...(tier !== undefined ? { tier } : {}),
          ...(credentialRef !== undefined ? { credentialRef } : {}),
          ...(targetUserId !== undefined ? { targetUserId } : {}),
        };
      }
    }

    // triggers takes full precedence over schedule shorthand
    // The backend does not understand a bare "schedule" field — build triggers here
    if (triggers !== undefined) {
      updates.triggers = triggers.map((t: any) =>
        t.type === 'cron' ? { ...t, timezone: 'UTC' } : t
      );
    } else if (schedule !== undefined) {
      // Fetch existing triggers to preserve non-cron entries
      try {
        const existing = await fetchService(`${API_BASE_URL}/automations/${automationId}`, { method: 'GET' });
        const existingTriggers = (existing.skeleton?.triggers || []).filter((t: any) => t.type !== 'cron');
        if (schedule.trim()) {
          updates.triggers = [
            ...existingTriggers,
            { type: 'cron', cron: schedule.trim(), humanFrequency: schedule.trim(), timezone: 'UTC' },
          ];
        } else {
          updates.triggers = existingTriggers.length > 0 ? existingTriggers : [{ type: 'manual' }];
        }
      } catch {
        updates.triggers = schedule.trim()
          ? [{ type: 'cron', cron: schedule.trim(), humanFrequency: schedule.trim(), timezone: 'UTC' }]
          : [{ type: 'manual' }];
      }
    }

    if (Object.keys(updates).length === 0) {
      return createErrorResult('No fields to update were provided', '', 'validation');
    }

    try {
      const updated = await fetchService(`${API_BASE_URL}/automations/${automationId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      return createSuccessResult(
        `Updated automation "${updated.name || name}"`,
        { automationId: updated._id || automationId, name: updated.name },
      );
    } catch (error: any) {
      return createErrorResult('Failed to update automation', error.message || 'Unknown error', 'execution');
    }
  },
};
